import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import moment from 'moment'
import { useNotificationStore } from '@/stores/helpers/notification.js'
import FarmerService from '@/services/admin/FarmerService.js'
import i18n from '@/i18n/i18n.js'
import { build_query, errorMessage } from '@/utilities/helpers.js'
import { FARMER_ERROR_MESSAGES } from '@/constants/errorMessages.js'

const { t } = i18n.global

export const allowedFilters = [
  'id',
  'retailers.id',
  'has_sales',
  'by_fullname_tckn_phone',
  'by_fullname_tckn_phone_businessnumber',
  'by_company_id',
  'verification_status',
  'has_fullname',
  'has_pending_document_control',
  'trashed'
]
export const allowedIncludes = 'retailers,companies,activePreApprovedLimitByMinLimit'
export const allowedSorts = 'firstname'
export const allowedAppends = 'sum_cash_price,sum_forward_price'

export const useFarmerStore = defineStore('adminFarmer', () => {
  // State
  const farmers = ref(null)
  const farmer = ref({})
  const meta = ref(null)
  const statistics = ref({})
  const gtsData = ref([])
  const gtsSalesHistories = ref([])
  const coupons = ref([])
  const previousGuaranteedApplications = ref([])
  const verificationStatusActivity = ref(null)
  const blacklistHistory = ref([])
  const activityHistory = ref([])

  // Getters
  const getFarmerById = computed(() => id => {
    return farmers.value ? farmers.value.find(f => f.id === id) : false
  })

  const notificationStore = useNotificationStore()

  // Actions
  const getFarmers = async query => {
    query['has_fullname'] = true
    const newQuery = build_query(query, allowedFilters, allowedIncludes, allowedSorts, allowedAppends)

    try {
      const response = await FarmerService.getFarmers(newQuery)
      farmers.value = response.data
      meta.value = response.meta
      return response
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: FARMER_ERROR_MESSAGES.getFarmersError + errorMessage(err)
      })
      throw err
    }
  }

  const getAllFarmers = async query => {
    const newQuery = build_query(query, allowedFilters, allowedIncludes, allowedSorts, allowedAppends)

    try {
      const response = await FarmerService.getFarmers(newQuery)
      return response
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: FARMER_ERROR_MESSAGES.getFarmersError + errorMessage(err)
      })
      throw err
    }
  }

  const getFarmer = async id => {
    if (id === farmer.value.id) {
      return farmer.value
    }

    const cachedFarmer = getFarmerById.value(id)
    if (cachedFarmer) {
      farmer.value = cachedFarmer
      return cachedFarmer
    }

    try {
      const response = await FarmerService.getFarmer(id)
      farmer.value = response.data
      return response.data
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: FARMER_ERROR_MESSAGES.getFarmerError + errorMessage(err)
      })
      throw err
    }
  }

  const refreshFarmer = async id => {
    try {
      const response = await FarmerService.getFarmer(id)
      farmer.value = response.data
      return response.data
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: errorMessage(err)
      })
      throw err
    }
  }

  const editFarmer = async farmerData => {
    try {
      const response = await FarmerService.editFarmer(farmerData.id, farmerData)
      let notification = {}

      if (response.data?.waitingApproval === true) {
        notification = {
          type: 'warning',
          message: t('store.shared.waiting_approval')
        }
      } else {
        farmer.value = response.data
        notification = {
          type: 'success',
          message: t('store.shared.updated', {
            element: t('store.admin.farmers.farmer_info')
          })
        }
      }

      notificationStore.add(notification)
      return response.data
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: errorMessage(err)
      })
      throw err
    }
  }

  const sendOtpForUpdatePhoneNumber = async payload => {
    try {
      const response = await FarmerService.sendOtpForUpdatePhoneNumber(payload.farmerId, payload)
      notificationStore.add({
        type: 'success',
        message: t('views.admin.farmers.otp_sent')
      })
      return response.data
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: errorMessage(err)
      })
      throw err
    }
  }

  const updatePhoneNumber = async payload => {
    try {
      const response = await FarmerService.updatePhoneNumber(payload.farmerId, payload)
      return response.data
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: errorMessage(err)
      })
      throw err
    }
  }

  const checkPhoneNumber = async payload => {
    try {
      const response = await FarmerService.checkPhoneNumber(payload.farmerId, payload)
      return response.data
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: errorMessage(err)
      })
      throw err
    }
  }

  const getFarmerVerificationActivity = async id => {
    try {
      const response = await FarmerService.getFarmerVerificationActivity(id)
      verificationStatusActivity.value = response.data
      return response.data
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: errorMessage(err)
      })
      throw err
    }
  }

  const updateFarmerCksStatus = async payload => {
    try {
      const response = await FarmerService.updateFarmerCksStatus(payload.farmerId, payload)
      farmer.value = response.data
      notificationStore.add({
        type: 'success',
        message: t('store.admin.farmers.update_farmer_cks_status_success')
      })
      return response.data
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: errorMessage(err)
      })
      throw err
    }
  }

  const getStatistics = async id => {
    try {
      const response = await FarmerService.getStatistics(id)
      statistics.value = response.data
      return response.data
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: FARMER_ERROR_MESSAGES.getStatisticsError + errorMessage(err)
      })
      throw err
    }
  }

  const getGtsData = async id => {
    try {
      const response = await FarmerService.getGtsData(id)
      gtsData.value = response.data.gts_data
      gtsSalesHistories.value = response.data.gts_sales_histories
      return response.data
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: FARMER_ERROR_MESSAGES.getGtsDataError + errorMessage(err)
      })
      throw err
    }
  }

  const checkGtsData = async id => {
    try {
      const response = await FarmerService.checkGtsData(id)
      notificationStore.add({
        type: 'info',
        message: t('store.admin.farmers.fetching_gts_data_please_wait')
      })
      return response
    } catch (err) {
      let message = FARMER_ERROR_MESSAGES.getGtsDataError
      if (err.response?.status === 409) {
        message += t('store.admin.farmers.may_a_search_is_working_for_the_farmer_already')
      }
      notificationStore.add({
        type: 'danger',
        message
      })
      throw err
    }
  }

  const getCoupons = async id => {
    try {
      const response = await FarmerService.getCoupons(id)
      coupons.value = response.data
      return response.data
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: FARMER_ERROR_MESSAGES.getCouponsError + errorMessage(err)
      })
      throw err
    }
  }

  const removeCoupon = async params => {
    try {
      const response = await FarmerService.removeCoupon(params.id, farmer.value.id)
      coupons.value = response.data
      notificationStore.add({
        type: 'success',
        message: t('store.shared.removed', {
          element: t('store.admin.farmers.coupon')
        })
      })
      return response.data
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: FARMER_ERROR_MESSAGES.removeCouponError + errorMessage(err)
      })
      throw err
    }
  }

  const checkPolicyAcceptance = async params => {
    try {
      const response = await FarmerService.checkPolicyAcceptance(params.tckn, params.phone)
      if (params.resend) {
        notificationStore.add({
          type: 'success',
          message: t('store.shared.send', {
            element: t('store.admin.farmers.otp')
          })
        })
      }
      return response.data
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: params.resend
          ? t('store.admin.farmers.an_error_occurred_while_sending_otp') + errorMessage(err)
          : t('store.admin.farmers.an_error_occurred_during_privacy_policy_check') + errorMessage(err)
      })
      throw err
    }
  }

  const verifyPolicyAcceptance = async params => {
    try {
      const response = await FarmerService.verifyPolicyAcceptance(params.tckn, params.code)
      if (response.data.is_verified) {
        return response.data
      } else {
        notificationStore.add({
          type: 'danger',
          message: t('store.retailer.farmer.invalid_confirmation_code')
        })
        throw response.data
      }
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: errorMessage(err)
      })
      throw err
    }
  }

  const getPreviousGuaranteedApplications = async application => {
    const newQuery = build_query(
      {
        'filter[beforeCreated]': moment(application.created_at).format('YYYY-MM-DD HH:mm'),
        'filter[status]': '1,2,3,4,5,6',
        per_page: 1000
      },
      ['farmer'],
      'order,order.payments,order.payments.collections,decisions,score',
      ['-created_at'],
      ['last_decision']
    )

    try {
      const response = await FarmerService.getPreviousGuaranteedApplications(application.farmer_id, newQuery)
      previousGuaranteedApplications.value = response.data
      return response.data
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: t('store.admin.farmers.an_error_occurred_while_getting_vouched_applications') + errorMessage(err)
      })
      throw err
    }
  }

  const checkGdprConsentDocumentByFarmerId = async id => {
    try {
      const response = await FarmerService.checkGdprConsentDocumentByFarmerId(id)
      if (!response || !response.document) {
        return null
      }
      return response.document
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: errorMessage(err)
      })
      throw err
    }
  }

  const downloadGdprDocument = async gdprConsentDocument => {
    try {
      const response = await FarmerService.downloadGdprDocument(gdprConsentDocument.id)
      const fileURL = window.URL.createObjectURL(new Blob([response]))
      const fileLink = document.createElement('a')

      fileLink.href = fileURL
      fileLink.setAttribute('download', gdprConsentDocument.document)
      document.body.appendChild(fileLink)
      fileLink.click()
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: errorMessage(err)
      })
      throw err
    }
  }

  const removePreApprovedLimit = async payload => {
    try {
      const response = await FarmerService.removePreApprovedLimit(payload.farmerId, payload)
      farmer.value = response.data
      notificationStore.add({
        type: 'success',
        message: t('store.admin.farmers.removed_pre_approved_limit_successfully')
      })
      return response.data
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: errorMessage(err)
      })
      throw err
    }
  }

  const getBlacklistHistory = async id => {
    try {
      const response = await FarmerService.getBlacklistHistory(id)
      blacklistHistory.value = response.data
      return response
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: errorMessage(err)
      })
      throw err
    }
  }

  const getActivityHistory = async id => {
    try {
      const response = await FarmerService.getActivityHistory(id)
      activityHistory.value = response.data
      return response
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: errorMessage(err)
      })
      throw err
    }
  }

  const inactivatePhoneNumber = async id => {
    try {
      const response = await FarmerService.inactivatePhoneNumber(id)
      farmer.value.phone = response.data.phone
      notificationStore.add({
        type: 'success',
        message: t('store.admin.farmers.phone_number_inactivated')
      })
      return response
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: errorMessage(err)
      })
      throw err
    }
  }

  const fetchNetworkAnalysis = params => {
    return new Promise((resolve, reject) => {
      FarmerService.fetchNetworkAnalysis(params.subjectType, params.subjectId, params.depth, params.dateRange)
        .then(response => {
          notificationStore.add({
            type: 'info',
            message: t('store.admin.farmers.device_network_report_requested')
          })
          resolve(response)
        })
        .catch(err => {
          notificationStore.add({
            type: 'danger',
            message: errorMessage(err)
          })
          reject(err)
        })
    })
  }

  return {
    farmers,
    farmer,
    meta,
    statistics,
    gtsData,
    gtsSalesHistories,
    coupons,
    previousGuaranteedApplications,
    verificationStatusActivity,
    blacklistHistory,
    activityHistory,
    getFarmerById,
    getFarmers,
    getAllFarmers,
    getFarmer,
    refreshFarmer,
    editFarmer,
    sendOtpForUpdatePhoneNumber,
    updatePhoneNumber,
    checkPhoneNumber,
    getFarmerVerificationActivity,
    updateFarmerCksStatus,
    getStatistics,
    getGtsData,
    checkGtsData,
    getCoupons,
    removeCoupon,
    checkPolicyAcceptance,
    verifyPolicyAcceptance,
    getPreviousGuaranteedApplications,
    checkGdprConsentDocumentByFarmerId,
    downloadGdprDocument,
    removePreApprovedLimit,
    getBlacklistHistory,
    getActivityHistory,
    inactivatePhoneNumber,
    fetchNetworkAnalysis
  }
})
